import React, { useContext, useState, memo } from "react";
import _ from "lodash";
import AppContext from "../../AppContext";
import { getCurrentOrder } from "../../selectors";
import {
  Container,
  SpacingBox,
  RouteButton,
  H2,
  Separator,
  H1,
  FlexBox,
  RedParagraph,
} from "../../Components";
import { getTranslateByScope } from "../../i18n";
import { routes } from "../../routes";
import { getProductsFromOrder, isOrderEditable } from "../../utils/order";
import Product from "./Product";
import CloseOrder from "./CloseOrder";
import SaveAsDraftButton from "./SaveAsDraft";
import UploadXLS from "./UploadXLS";
import { orderStatus } from "../../constants";
import SyncedOrderBanner from "./SyncedOrderBanner";

const translate = getTranslateByScope("app.currentOrder");

const getOrderAndProducts = (context) => {
  const order = getCurrentOrder(context) ? getCurrentOrder(context).doc : null;
  if (!order) return {};
  const products = getProductsFromOrder(order);
  return { order, products };
};

const getOpenProducts = (order, products) => {
  if (!order) {
    return [];
  }
  let actualOpen = [];
  products.forEach((p) => {
    const id = p.id;
    const hasData = _.get(order, `products.${id}.data`);
    const isOpen = actualOpen.includes(id);

    if (!hasData && !isOpen) {
      actualOpen.push(id);
    }
  });
  return actualOpen;
};

export const CurrentOrder = () => {
  const context = useContext(AppContext);
  const d = getOrderAndProducts(context);
  const order = d.order;
  const products = d.products;
  const openProducts = getOpenProducts(order, products);
  const [open, setOpen] = useState(openProducts);
  const isReadonly = !isOrderEditable(order);
  const hasOrderType = Boolean(order?.orderType?.value);

  if (!order) {
    return (
      <FlexBox justifyContent="center">
        <H1>No order</H1>
      </FlexBox>
    );
  }

  return (
    <div className="margin-10p">
      <Container>
        <SpacingBox marginTop="xl">
          <SyncedOrderBanner />
          <div className="row">
            <div className="col-12 col-lg-7">
              {<H2>{translate("title", { number: order.orderNumber })}</H2>}
              {!hasOrderType && (
                <SpacingBox marginTop="md">
                  <RedParagraph>
                    {translate("missingOrderType.text")}
                  </RedParagraph>
                  {!isReadonly && (
                    <RouteButton
                      text={translate("missingOrderType.editOrderHeader")}
                      routeTo={routes.orderUpdate(order.id)}
                    />
                  )}
                </SpacingBox>
              )}
              {!isReadonly && (
                <SpacingBox marginTop="lg">
                  <RouteButton
                    text={translate("addProduct")}
                    routeTo={routes.productList}
                  />
                </SpacingBox>
              )}
              {products
                .filter((p) => p.id || p.number)
                .map((p) => (
                  <div key={p.id}>
                    <Product
                      tischOffensiveId={p.tischOffensiveId}
                      open={open}
                      setOpen={setOpen}
                      deliveryDate={p.deliveryDate}
                      key={p.id + _.get(p, "deliveryDate.value")}
                      notes={p.notes}
                      productName={p.name}
                      productNumber={p.number}
                      productSeason={p.season}
                      id={p.id}
                      data={p.data}
                    />
                    <SpacingBox paddingTop="md" paddingBottom="md">
                      <Separator />
                    </SpacingBox>
                  </div>
                ))}
            </div>
            <div className="col-12 col-lg-5">
              <div className="sticky-top" style={{ zIndex: 1 }}>
                {(order.status === orderStatus.unsynced ||
                  order.status === orderStatus.draft) && (
                  <FlexBox
                    marginTop="md"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <UploadXLS />
                    <SaveAsDraftButton />
                  </FlexBox>
                )}
                <div style={{ height: "20px" }} />
                <CloseOrder />
              </div>
            </div>
          </div>
        </SpacingBox>
      </Container>
    </div>
  );
};

export default memo(CurrentOrder);
